export interface PageContext {
  url: string;
  title?: string;
  referrer?: string;
  language?: string;
}

export interface PageTracker {
  /** The page the shopper is on right now — stamped on every message they send. */
  current(): PageContext;
  stop(): void;
}

function readPage(): PageContext {
  return {
    url: window.location.href,
    title: document.title || undefined,
    referrer: document.referrer || undefined,
    language: navigator.language || undefined,
  };
}

/**
 * Page tracking across SPA navigations (PLAN.md "Session & visitor
 * tracking": hooking `pushState`/`replaceState`/`popstate`). Client sites
 * are overwhelmingly SPAs now, and without these hooks a page journey on
 * one would be a single entry — the URL the widget happened to load on.
 *
 * Patching history is intrusive, so this is careful about it: the original
 * functions are captured and restored on `stop()`, and a page change is
 * reported only when the URL actually differs (frameworks call
 * `replaceState` on every render).
 */
export function trackPageViews(onView: (page: PageContext, isSpaNav: boolean) => void): PageTracker {
  let current = readPage();
  onView(current, false);

  const report = () => {
    const next = readPage();
    if (next.url === current.url) return;
    current = next;
    onView(next, true);
  };

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function patchedPushState(...args: Parameters<History["pushState"]>) {
    originalPushState(...args);
    // After the frame, so `document.title` reflects the page the router
    // just rendered rather than the one it left.
    setTimeout(report, 0);
  };
  history.replaceState = function patchedReplaceState(...args: Parameters<History["replaceState"]>) {
    originalReplaceState(...args);
    setTimeout(report, 0);
  };

  const onPopState = () => setTimeout(report, 0);
  window.addEventListener("popstate", onPopState);

  return {
    current: () => current,
    stop: () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", onPopState);
    },
  };
}
